import { useEffect, useState } from 'react';
import type { FieldContent } from '@capture/shared';

interface Props {
  field: FieldContent;
  format: 'xml' | 'json';
  onPathChange: (next: string) => void;
  onRequiredChange: (next: boolean) => void;
  onRemove: () => void;
}

/**
 * Single row in the HeaderFieldsPane list. Assumes the field's first
 * `import`-stage behavior is a `populate` with a `header-xpath` or
 * `header-path` source — callers enforce this by filtering via
 * `classifyField(f) === 'header'` before rendering.
 *
 * Path input uses local draft state synced to props (same pattern as
 * IdentityEditor) so debounced parent PATCH doesn't reset mid-typing.
 */
export function HeaderFieldRow({
  field,
  format,
  onPathChange,
  onRequiredChange,
  onRemove,
}: Props) {
  const firstBehavior = field.stageMap.import?.[0];
  const src =
    firstBehavior && firstBehavior.kind === 'populate'
      ? firstBehavior.source
      : null;
  const initialPath =
    src && (src.type === 'header-xpath' || src.type === 'header-path')
      ? format === 'xml'
        ? (src as { type: 'header-xpath'; xpath: string }).xpath
        : (src as { type: 'header-path'; path: string }).path
      : '';
  const required =
    src && (src.type === 'header-xpath' || src.type === 'header-path')
      ? Boolean((src as { required?: boolean }).required)
      : false;

  const [pathDraft, setPathDraft] = useState(initialPath);
  useEffect(() => setPathDraft(initialPath), [initialPath]);

  return (
    <div className="header-field-row">
      <span
        className="header-field-row__scope-badge"
        aria-label="Batch-scoped field"
      >
        BATCH
      </span>
      <div className="header-field-row__name">{field.name}</div>
      <input
        className="header-field-row__path"
        value={pathDraft}
        onChange={(e) => {
          setPathDraft(e.target.value);
          onPathChange(e.target.value);
        }}
        placeholder={format === 'xml' ? '/R/@id' : 'meta.id'}
        aria-label={`${field.name} path`}
      />
      <label className="header-field-row__required">
        <input
          type="checkbox"
          checked={required}
          onChange={(e) => onRequiredChange(e.target.checked)}
        />
        required
      </label>
      <button
        type="button"
        className="header-field-row__remove"
        onClick={onRemove}
        aria-label={`Remove ${field.name}`}
      >
        Remove
      </button>
    </div>
  );
}
